/**
 * Which language the app starts in, and who got to decide.
 *
 * There are two answers and they are not the same thing. A person who has used
 * the EN/DE switch has *chosen*, and nothing may override that. Everybody else
 * has merely never been asked — and for them the app used to start in English
 * regardless, which is why a German business unit's colleagues all met an English
 * interface and had to fix it one browser at a time. PRD H7 makes that
 * starting point the business unit's to set.
 *
 * **Why a second storage key rather than reading `i18nextLng`.** That key is
 * not evidence of a choice: i18next writes it during `init`, so it holds "en"
 * for everyone who has ever loaded the page, including the people who never
 * touched the switch. Treating it as a preference would mean the BU default
 * could never apply to anybody. So the deliberate act gets its own key, written
 * only by `chooseLanguage`, and `i18nextLng` stays what it is — the last
 * language rendered, which is what makes a reload not flicker.
 *
 * The unit's default is therefore a *default*, in the full sense: it decides
 * where somebody starts and loses to their own choice forever after. It is also
 * per browser, like the choice it defers to — there is no per-account language
 * field, so a colleague on a second machine starts from the unit's default
 * again rather than from what they picked on the first.
 */

import type { i18n as I18n } from "i18next";

/** The languages the app has bundles for. Kept in step with `i18n.ts`'s
 *  `resources` and with the rules' `validDefaultLanguage`; a tag outside this
 *  set resolves to nothing and renders raw keys. */
export const SUPPORTED_LANGUAGES = ["en", "de"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** The language the app falls back to when nobody — neither the viewer nor
 *  their business unit — has said anything. */
export const FALLBACK_LANGUAGE: SupportedLanguage = "en";

/** Marks that the viewer picked a language themselves. Deliberately not
 *  `i18nextLng` — see the module comment. */
const CHOICE_KEY = "navigatorLangChoice";

/**
 * `value` as a language we can actually render, or null.
 *
 * Prefix-matched, so a stored `de-DE` (or a BU document typed by hand) reads as
 * German rather than as nothing. Matching the whole tag was the version of this
 * that made `i18n.ts` treat every regional variant as English.
 */
export function asSupportedLanguage(value: unknown): SupportedLanguage | null {
  if (typeof value !== "string") return null;
  const tag = value.trim().toLowerCase();
  return SUPPORTED_LANGUAGES.find((lang) => tag.startsWith(lang)) ?? null;
}

/**
 * The language the viewer chose for themselves, or null if they never did.
 *
 * Reads are wrapped because `localStorage` throws outright in some contexts
 * (a browser set to block site data), and a language lookup must never be what
 * stops the app from mounting.
 */
export function chosenLanguage(): SupportedLanguage | null {
  try {
    return asSupportedLanguage(localStorage.getItem(CHOICE_KEY));
  } catch {
    return null;
  }
}

/**
 * Switch language *because the viewer said so* — the EN/DE control's one entry
 * point.
 *
 * Recording the choice is the whole reason this exists rather than calling
 * `changeLanguage` directly: without the record, the next page load would
 * re-apply the unit's default over the top of what they just picked, and the
 * switch would look broken every time it disagreed with the unit.
 */
export async function chooseLanguage(
  i18n: I18n,
  language: SupportedLanguage,
): Promise<void> {
  try {
    localStorage.setItem(CHOICE_KEY, language);
  } catch {
    // A viewer who cannot persist anything still gets the switch for this
    // session; they just start from the unit's default again next time.
  }
  await i18n.changeLanguage(language);
}

/**
 * Apply a business unit's default to a viewer who has not chosen.
 *
 * Returns whether it changed anything, which is what makes it safe to call on
 * every snapshot of the BU document: a unit whose default already matches, a
 * viewer who has chosen, and a unit that has configured nothing all do nothing
 * at all.
 *
 * A unit *changing* its default does reach the people who never chose, and
 * that is intended — it is the setting's only way to fix a business unit that was
 * configured wrong, and the people it moves are precisely the ones who
 * expressed no opinion.
 */
export function applyBusinessUnitDefault(
  i18n: I18n,
  defaultLanguage: unknown,
): boolean {
  if (chosenLanguage()) return false;
  const language = asSupportedLanguage(defaultLanguage);
  if (!language) return false;
  if (
    asSupportedLanguage(i18n.resolvedLanguage ?? i18n.language) === language
  ) {
    return false;
  }
  void i18n.changeLanguage(language);
  return true;
}
